import { getCurrentUser } from '@/lib/auth-utils'
import { ROLE_ADMIN } from '@/lib/permissions'
import type { Company } from '@/models'
import { renderTemplate, sendEmail } from '@/services/email'
import { neon } from '@/services/neon'

async function checkCompanyOwner(companyId: string): Promise<void> {
  const user = getCurrentUser()
  if (!user?.id) throw new Error('Not authenticated.')

  if (user.role === ROLE_ADMIN) return

  const { data } = await neon
    .from('companies')
    .select('owner')
    .eq('id', companyId)
    .is('deleted_at', null)
    .single()
  const ownerId = (data as { owner?: string } | null)?.owner

  if (ownerId !== user.id) {
    throw new Error('You do not have permission to manage members for this company.')
  }
}

async function checkCompanyAccess(companyId: string): Promise<void> {
  const user = getCurrentUser()
  if (!user?.id) throw new Error('Not authenticated.')

  if (user.role === ROLE_ADMIN) return

  const { data } = await neon
    .from('companies')
    .select('owner')
    .eq('id', companyId)
    .is('deleted_at', null)
    .single()
  const ownerId = (data as { owner?: string } | null)?.owner

  if (ownerId === user.id) return

  const { data: member } = await neon
    .from('company_members')
    .select('role')
    .eq('company_id', companyId)
    .eq('user_id', user.id)
    .eq('is_active', true)
    .single()
  const role = (member as { role?: string } | null)?.role

  if (!role || (role !== 'owner' && role !== 'manager')) {
    throw new Error('You do not have permission to access this company.')
  }
}

async function withOwnerNames(companies: Company[]): Promise<Company[]> {
  const ownerIds = Array.from(
    new Set(companies.map((company) => company.owner).filter((owner): owner is string => !!owner)),
  )

  if (ownerIds.length === 0) {
    return companies
  }

  try {
    const { data, error } = await neon.from('users').select('id,name').in('id', ownerIds)
    if (error) {
      throw new Error(error.message)
    }

    const names = new Map(
      ((data as Array<{ id: string; name?: string }> | null) || []).map((row) => [
        row.id,
        row.name,
      ]),
    )

    return companies.map((company) => ({
      ...company,
      owner_name: company.owner
        ? names.get(company.owner) || company.owner_name
        : company.owner_name,
    }))
  } catch {
    return companies
  }
}

async function fetchAllCompaniesInternal(): Promise<Company[]> {
  const { data, error } = await neon.from('companies').select('*').is('deleted_at', null)
  if (error) {
    throw new Error(error.message)
  }

  return withOwnerNames((data as Company[]) || [])
}

async function fetchMyCompaniesInternal(userId: string): Promise<Company[]> {
  const [ownedRes, membershipRes] = await Promise.all([
    neon.from('companies').select('*').eq('owner', userId).is('deleted_at', null),
    neon
      .from('company_members')
      .select('company_id,role')
      .eq('user_id', userId)
      .eq('is_active', true),
  ])

  if (ownedRes.error) {
    throw new Error(ownedRes.error.message)
  }

  const memberCompanyIds = (
    (membershipRes.data as Array<{ company_id?: string; role?: string }> | null) || []
  )
    .filter((row) => row.company_id && (row.role === 'owner' || row.role === 'manager'))
    .map((row) => row.company_id as string)

  let memberCompanies: Company[] = []
  if (memberCompanyIds.length > 0) {
    const memberRes = await neon
      .from('companies')
      .select('*')
      .in('id', memberCompanyIds)
      .is('deleted_at', null)
    if (!memberRes.error) {
      memberCompanies = (memberRes.data as Company[]) || []
    }
  }

  const seen = new Set<string>()
  const companies = [...(ownedRes.data as Company[]), ...memberCompanies].filter((c) => {
    if (seen.has(c.id)) return false
    seen.add(c.id)
    return true
  })
  return withOwnerNames(companies)
}

export async function fetchAllCompanies(): Promise<Company[]> {
  const user = getCurrentUser()
  if (!user?.id || user.role !== ROLE_ADMIN) {
    throw new Error('You do not have permission to view all companies.')
  }
  return fetchAllCompaniesInternal()
}

export async function fetchMyCompanies(): Promise<Company[]> {
  const currentUser = getCurrentUser()

  if (!currentUser?.id) {
    return []
  }

  return fetchMyCompaniesInternal(currentUser.id)
}

export async function fetchCompanies(): Promise<Company[]> {
  const currentUser = getCurrentUser()

  if (!currentUser || currentUser.role === ROLE_ADMIN) {
    return fetchAllCompaniesInternal()
  }

  if (!currentUser.id) {
    return []
  }

  return fetchMyCompaniesInternal(currentUser.id)
}

export async function createCompany(company: Partial<Company>): Promise<Company> {
  const currentUser = getCurrentUser()
  if (!currentUser || currentUser.role !== ROLE_ADMIN) {
    throw new Error('You do not have permission to add companies.')
  }

  const owner = company.owner || currentUser?.id
  const payload = owner
    ? {
        ...company,
        owner,
      }
    : { ...company }

  const { data, error } = await neon.from('companies').insert(payload).select().single()
  if (error) {
    throw new Error(error.message)
  }
  if (!data) {
    throw new Error('Company creation returned no data')
  }

  const created = data as Company

  const ownerId = created.owner || currentUser?.id
  if (ownerId) {
    const { error: memberError } = await neon
      .from('company_members')
      .upsert(
        { company_id: created.id, user_id: ownerId, role: 'owner', is_active: true },
        { onConflict: 'company_id,user_id' },
      )
    if (memberError) {
      throw new Error(`Company created but failed to set owner membership: ${memberError.message}`)
    }
  }

  return created
}

export async function updateCompany(id: string, company: Partial<Company>): Promise<Company> {
  const currentUser = getCurrentUser()

  if (!currentUser || currentUser.role !== ROLE_ADMIN) {
    if (!currentUser) {
      throw new Error('You do not have permission to edit this company.')
    }
    const { data, error } = await neon
      .from('company_members')
      .select('role')
      .eq('company_id', id)
      .eq('user_id', currentUser.id)
      .eq('is_active', true)
      .single()

    const role = !error ? (data as { role?: string } | null)?.role : null
    if (role !== 'owner') {
      throw new Error('You do not have permission to edit this company.')
    }
  }

  const { data, error } = await neon
    .from('companies')
    .update(company)
    .eq('id', id)
    .is('deleted_at', null)
    .select()
    .single()

  if (error) {
    const isNotFound =
      (error as any).code === 'PGRST116' || (error as any).details?.includes('0 rows')
    if (isNotFound) throw new Error('Company not found or permission revoked.')
    throw new Error(error.message)
  }
  if (!data) {
    throw new Error('Company update returned no data')
  }
  return data as Company
}

export async function deleteCompany(id: string): Promise<void> {
  const currentUser = getCurrentUser()
  if (!currentUser || currentUser.role !== ROLE_ADMIN) {
    throw new Error('You do not have permission to delete companies.')
  }

  const { error, data } = await (neon.from('companies') as any)
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)
    .is('deleted_at', null)
    .select()
    .single()
  if (error) {
    const isNotFound =
      (error as any).code === 'PGRST116' || (error as any).details?.includes('0 rows')
    if (isNotFound) return
    throw new Error(error.message)
  }
  if (!data) return
}

export async function fetchCompanyById(id: string): Promise<Company | null> {
  await checkCompanyAccess(id)

  const { data, error } = await neon
    .from('companies')
    .select('*')
    .eq('id', id)
    .is('deleted_at', null)
    .single()
  if (error || !data) return null
  const results = await withOwnerNames([data as Company])
  return results[0] ?? null
}

export interface CompanyMember {
  user_id: string
  role: string
  is_active: boolean
  name?: string
  email?: string
}

export async function fetchCompanyMembers(companyId: string): Promise<CompanyMember[]> {
  await checkCompanyAccess(companyId)

  const { data: companyData } = await neon
    .from('companies')
    .select('owner')
    .eq('id', companyId)
    .single()
  const ownerId = (companyData as { owner?: string } | null)?.owner

  const { data, error } = await neon
    .from('company_members')
    .select('user_id,role,is_active')
    .eq('company_id', companyId)

  let members: Array<{ user_id: string; role: string; is_active: boolean }> =
    !error && data ? (data as Array<{ user_id: string; role: string; is_active: boolean }>) : []

  if (ownerId && !members.some((member) => member.user_id === ownerId)) {
    members = [...members, { user_id: ownerId, role: 'owner', is_active: true }]
  }

  const userIds = members.map((m) => m.user_id)
  if (userIds.length === 0) return []

  const { data: users } = await neon.from('users').select('id,name,email').in('id', userIds)
  const userMap = new Map(
    ((users as Array<{ id: string; name?: string; email?: string }> | null) || []).map((u) => [
      u.id,
      u,
    ]),
  )

  return members.map((m) => ({
    ...m,
    name: userMap.get(m.user_id)?.name,
    email: userMap.get(m.user_id)?.email,
  }))
}

export async function addCompanyMember(
  companyId: string,
  userId: string,
  role: string = 'manager',
): Promise<void> {
  await checkCompanyOwner(companyId)

  const { error } = await neon
    .from('company_members')
    .upsert(
      { company_id: companyId, user_id: userId, role, is_active: true },
      { onConflict: 'company_id,user_id' },
    )
  if (error) throw new Error(error.message)
}

export async function removeCompanyMember(companyId: string, userId: string): Promise<void> {
  await checkCompanyOwner(companyId)

  const { error } = await neon
    .from('company_members')
    .delete()
    .eq('company_id', companyId)
    .eq('user_id', userId)
  if (error) throw new Error(error.message)
}

export async function transferCompanyOwnership(
  companyId: string,
  newOwnerUserId: string,
): Promise<Company> {
  const currentUser = getCurrentUser()
  if (!currentUser) {
    throw new Error('You must be logged in to transfer ownership.')
  }

  if (currentUser.role !== ROLE_ADMIN) {
    const { data, error } = await neon
      .from('company_members')
      .select('role')
      .eq('company_id', companyId)
      .eq('user_id', currentUser.id)
      .eq('is_active', true)
      .single()
    const role = !error ? (data as { role?: string } | null)?.role : null
    if (role !== 'owner') {
      throw new Error('Only the current owner or an admin can transfer ownership.')
    }
  }

  const { data: companyData, error: fetchError } = await neon
    .from('companies')
    .select('owner')
    .eq('id', companyId)
    .single()
  if (fetchError || !companyData) {
    throw new Error('Company not found.')
  }
  const oldOwnerId = (companyData as { owner?: string }).owner
  if (oldOwnerId === newOwnerUserId) {
    throw new Error('This user is already the owner.')
  }

  const { error: updateError } = await neon
    .from('companies')
    .update({ owner: newOwnerUserId })
    .eq('id', companyId)
  if (updateError) {
    throw new Error(`Failed to transfer ownership: ${updateError.message}`)
  }

  await addCompanyMember(companyId, newOwnerUserId, 'owner')

  if (oldOwnerId) {
    await removeCompanyMember(companyId, oldOwnerId)
    await addCompanyMember(companyId, oldOwnerId, 'manager')
  }

  const updated = await fetchCompanyById(companyId)
  if (!updated) {
    throw new Error('Company not found after transfer.')
  }
  return updated
}

export async function findUserByEmail(
  email: string,
): Promise<{ id: string; name?: string; email?: string } | null> {
  const { data, error } = await neon
    .from('users')
    .select('id,name,email')
    .eq('email', email)
    .single()
  if (error || !data) return null
  return data as { id: string; name?: string; email?: string }
}

export async function inviteUserToCompany(
  companyId: string,
  email: string,
  name?: string,
  companyName?: string,
): Promise<{ userId: string; isNew: boolean }> {
  if (!email?.trim()) throw new Error('Email is required.')
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(email.trim())) throw new Error('Invalid email format.')

  await checkCompanyOwner(companyId)

  const existing = await findUserByEmail(email.trim())
  if (existing) {
    await addCompanyMember(companyId, existing.id, 'manager')
    return { userId: existing.id, isNew: false }
  }

  const tempPassword = (globalThis.crypto as any)?.randomUUID
    ? (globalThis.crypto as any).randomUUID().slice(0, 16)
    : Math.random().toString(36).slice(2, 18)

  const res: any = await neon.auth.signUp.email({
    name: name?.trim() || email.split('@')[0] || '',
    email: email.trim(),
    password: tempPassword,
  })
  if (res.error) {
    throw new Error(res.error.message ?? 'Failed to create account')
  }
  const createdUserId = (res.data?.user as { id?: string } | null)?.id
  if (!createdUserId) throw new Error('Failed to create account — no user id returned.')

  // Approve and scope to company without requiring admin (owner can invite)
  let approved = false
  try {
    const { error: approveError } = await neon
      .from('users')
      .update({ is_approved: true, role: 'manager', company_id: companyId } as any)
      .eq('id', createdUserId)
    if (approveError) throw new Error(approveError.message)
    approved = true
    await addCompanyMember(companyId, createdUserId, 'manager')

    // Best-effort invite email via shared sender
    try {
      const { data: tplData } = await neon
        .from('email_templates')
        .select('subject,body')
        .eq('key', 'invite_user')
        .single()
      if (tplData) {
        const { subject, html } = renderTemplate(tplData as { subject: string; body: string }, {
          name: name?.trim() || email.split('@')[0] || '',
          company_name: companyName || '',
          temp_password: tempPassword,
          login_url: typeof window !== 'undefined' ? window.location.origin + '/login' : '/login',
        })
        await sendEmail(email.trim(), subject, html)
      }
    } catch {
      // best-effort
    }

    return { userId: createdUserId, isNew: true }
  } catch (e) {
    // Cleanup created user if approval or member add failed
    if (createdUserId && !approved) {
      // If approval failed, user exists but not yet linked — clean up
      try {
        await neon.from('users').delete().eq('id', createdUserId)
      } catch {
        // best-effort
      }
    } else if (createdUserId) {
      try {
        await neon.from('users').delete().eq('id', createdUserId)
      } catch {
        // best-effort
      }
    }
    throw e
  }
}
